import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Text, Portal } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import NoticeCard from "./NoticeCard";
import { useTranslation } from "react-i18next";
import { useNoticeContext } from "@root/context/notice-context";
import { useNoticeTypeContext } from "@root/context/notice-type-context";
import CustomSelect from "@root/components/common/CustomSelect";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE, ROUTE_NAMES } from "@root/constants";
import { useJournalistNoticeTourGuide } from "@root/hooks/useJournalistNoticeTourGuide";
import { usePledgeNoticeTourGuide } from "@root/hooks/usePledgeNoticeTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

const Notice: React.FC = () => {
  const {
    publishNoticeData,
    fetchPublishNotice,
    publishNoticePageNo,
    setPublishNoticePageNo,
    publishNoticePageCount,
  } = useNoticeContext();
  const { noticeTypeData, fetchNoticeType } = useNoticeTypeContext();
  const { slug } = useOrganizationBranding();
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const [selectedNoticeType, setSelectedNoticeType] = useState("");
  const [noticeTypeId, setNoticeTypeId] = useState("");
  const { journalistNoticeTourGuide, isMobile: isMobileJournalist } =
    useJournalistNoticeTourGuide();
  const { pledgeNoticeTourGuide, isMobile: isMobilePledge } =
    usePledgeNoticeTourGuide();
  const isOnPledgeNotice = pathname === ROUTE_NAMES.PLEDGE_ALL_NOTICES;
  const noticeTourGuide = isOnPledgeNotice
    ? pledgeNoticeTourGuide
    : journalistNoticeTourGuide;
  const isMobileTour = isOnPledgeNotice ? isMobilePledge : isMobileJournalist;

  useEffect(() => {
    fetchPublishNotice("", publishNoticePageNo, noticeTypeId, slug || undefined); // pass slug directly to avoid stale closure
  }, [publishNoticePageNo, noticeTypeId, slug]);

  useEffect(() => {
    fetchNoticeType({});
  }, []);

  // const filteredNoticeData = selectedNoticeType
  //   ? publishNoticeData?.filter(
  //       (notice: any) => String(notice.noticeTypeId) === selectedNoticeType
  //     )
  //   : publishNoticeData;
  function renderNoticeCard() {
    return (
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={5}>
        {publishNoticeData?.map((notice: any, idx: any) => (
          <NoticeCard key={idx} notice={notice} />
        ))}
      </Grid>
    );
  }

  const handleNoticeTypeChange = (value: string) => {
    const filter = noticeTypeData?.filter(
      (item: any) => item.id == Number(value),
    );
    console.log("filter", filter[0]?.name);
    setNoticeTypeId(value);
    setPublishNoticePageNo(1);
    setSelectedNoticeType(filter[0]?.name);
  };

  return (
    <Box py={4} px={8} bg="#fafbfc" minW={"full"} minH="100vh">
      <Flex justifyContent={"space-between"} mb={4}>
        <Box>
          <Text fontWeight={600} fontSize="24px" color="#2F2F34" mb={1}>
            {t("notice.noticesTitle")}
          </Text>
          <Text color="#798692" fontWeight={400} fontSize="16px" mb={6}>
            {t("notice.noticesSubtitle")}
          </Text>
        </Box>
        <Box data-tour-item="journalist-notice-filter">
          <CustomSelect
            placeholder={t("userStatus.all")}
            value={noticeTypeId}
            onChange={handleNoticeTypeChange}
            mt={4}
            width={"fit-content"}
            borderRadius={"62px"}
            color={"#5C6670"}
            border={"1px solid #5C6670"}
            options={
              noticeTypeData?.map((item: any) => ({
                value: String(item?.id),
                label: item?.name,
              })) || []
            }
          />
        </Box>
      </Flex>
      {renderNoticeCard()}
      {/* Pagination */}
      {publishNoticePageCount > 0 && (
        <Box mt={6} display="flex" justifyContent="center">
          <Paginate
            marginTop={0}
            page={publishNoticePageNo}
            count={publishNoticePageCount}
            pageSize={RECORDS_PER_PAGE}
            onPageChange={(newPage: number) => {
              setPublishNoticePageNo(newPage);
            }}
            variant="ghost"
          />
        </Box>
      )}

      {!isMobileTour && !noticeTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={noticeTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}

      {!isMobileTour &&
        noticeTourGuide.isActive &&
        noticeTourGuide.currentStepData && (
          <TourGuide
            isActive={noticeTourGuide.isActive}
            currentStep={noticeTourGuide.currentStepData}
            currentStepIndex={noticeTourGuide.currentStep}
            totalSteps={noticeTourGuide.totalSteps}
            onNext={noticeTourGuide.nextStep}
            onBack={noticeTourGuide.previousStep}
            onClose={noticeTourGuide.stopTour}
            targetSelector={noticeTourGuide.currentStepData.targetSelector}
            borderRadius="8px"
          />
        )}
    </Box>
  );
};

export default Notice;
